import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { Btn, Empty } from "../../components/UI";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { Plus, Pencil, Trash2, AlertTriangle, LineChart, X } from "lucide-react";
import { LineChart as ReChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import CodeAutocomplete from "./CodeAutocomplete";
import { ProviderSelect } from "../../components/ProviderSelect";
import { toast } from "sonner";

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");
const chip = (color) => `rounded-full px-2 py-0.5 text-[10px] font-bold ${color}`;
const STATUS_TONE = {
  Ordered: "bg-[#E3F2FD] text-[#1565C0]",
  Collected: "bg-[#FFF3E0] text-[#8D5A00]",
  "In Progress": "bg-[#FFF3E0] text-[#8D5A00]",
  Resulted: "bg-[#E8F5E9] text-[#2E7D32]",
  Cancelled: "bg-[#FFEBEE] text-[#C62828]",
};
const PRIO_TONE = {
  Routine: "bg-[#EFEBE0] text-[#6D4C41]",
  ASAP: "bg-[#FFF3E0] text-[#8D5A00]",
  STAT: "bg-[#FFEBEE] text-[#C62828]",
};

const EMPTY = { test_name: "", loinc_code: "", order_date: "", ordering_provider_name: "",
                priority: "Routine", status: "Ordered", instruction: "", indication: "",
                specimen_type: "", fasting: false, reflex_tests: "",
                collected_date: "", collector: "", collection_site: "",
                result_date: "", result_value: "", reference_range: "",
                abnormal_flag: "", result_status: "Final", interpretation: "",
                auto_repeat_interval_days: "", reference_lab: "", notes: "" };

const SPECIMEN_TYPES = ["", "Whole Blood", "Serum", "Plasma", "Urine (random)", "Urine (24-hour)",
                         "Saliva", "Stool", "CSF", "Buccal Swab", "Nasal Swab", "Throat Swab", "Sputum",
                         "Wound Culture", "Tissue Biopsy", "Hair Sample"];
const RESULT_STATUSES = ["Preliminary", "Final", "Corrected", "Amended", "Cancelled"];

const ABNORMAL_FLAGS = ["", "N (Normal)", "L (Low)", "H (High)", "LL (Critically Low)", "HH (Critically High)", "A (Abnormal)"];

const TrendDot = (props) => {
  const { cx, cy, payload, index, testKey } = props;
  const oor = payload?.out_of_range;
  return (
    <g key={`dot-${testKey}-${index}`}>
      <circle cx={cx} cy={cy} r={oor ? 5 : 3.5}
        fill={oor ? "#C62828" : "#4E342E"}
        stroke={oor ? "#C62828" : "#4E342E"} strokeWidth={oor ? 1 : 0}
        data-testid={`lab-trend-dot-${testKey}-${index}${oor ? "-oor" : ""}`} />
      {oor && (
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="#C62828">!</text>
      )}
    </g>
  );
};

export default function LabOrderList({ client, editable, dict }) {
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const [trends, setTrends] = useState(null);
  const [trendsOpen, setTrendsOpen] = useState(false);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const load = () => api.get(`/clients/${client.id}/lab-orders`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line
  const openTrends = async () => {
    setTrendsOpen(true); setTrends(null);
    try { const r = await api.get(`/clients/${client.id}/lab-trends`); setTrends(r.data); }
    catch (err) { toast.error(apiError(err)); }
  };
  const filtered = useMemo(() => items.filter((l) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [l.test_name, l.loinc_code, l.status, l.priority, l.ordering_provider_name, l.indication, l.lab_order_number].some((x) => (x || "").toLowerCase().includes(s));
  }), [items, q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.test_name.trim()) return toast.error("Test name is required");
    try {
      if (editId) await api.put(`/lab-orders/${editId}`, form);
      else await api.post(`/clients/${client.id}/lab-orders`, form);
      toast.success(editId ? "Lab order updated" : "Lab order added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    try { await api.delete(`/lab-orders/${del.id}`); toast.success("Lab order removed"); setDel(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const statuses = dict?.lab_statuses || ["Ordered", "Collected", "In Progress", "Resulted", "Cancelled"];
  const priorities = dict?.lab_priorities || ["Routine", "ASAP", "STAT"];

  return (
    <div data-testid="lab-orders-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#3E2723]">All Lab Orders</h3>
          <p className="text-xs text-[#7B6157]">{filtered.length} entr{filtered.length === 1 ? "y" : "ies"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="lab-filter" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
            className="rounded-full border border-[#E5DFD3] bg-white px-3.5 py-1.5 text-xs outline-none w-44 focus:border-[#8D6E63]" />
          <Btn variant="outline" data-testid="lab-trends-btn" onClick={openTrends}><LineChart size={14} /> Trends</Btn>
          {editable && <Btn data-testid="add-lab-btn" variant="outline"
            onClick={() => { setForm({ ...EMPTY, order_date: dayjs().format("YYYY-MM-DD") }); setEditId(null); setPanel(true); }}>
            <Plus size={14} /> Add New Lab Orders</Btn>}
        </div>
      </div>

      {filtered.length === 0 ? <Empty text="No lab orders on file" /> : (
        <div className="overflow-x-auto rounded-2xl border border-[#EFEBE0]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FDFCF8]">
              <tr className="text-left text-[10px] font-bold uppercase text-[#7B6157]">
                {["Lab Order #", "Order Date", "Test Name", "LOINC", "Priority", "Status", "Ordering Provider", "Instruction", "Indication", "Result", "Actions"].map((h) => (
                  <th key={h} className={`px-3 py-3 whitespace-nowrap ${h === "Actions" ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`lab-row-${l.id}`} className="border-t border-[#EFEBE0]">
                  <td className="px-3 py-2 font-mono text-[13px] text-[#3E2723]">{l.lab_order_number}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{fmt(l.order_date)}</td>
                  <td className="px-3 py-2 font-semibold text-[#3E2723]">{l.test_name}</td>
                  <td className="px-3 py-2 font-mono text-[13px] text-[#7B6157]">{l.loinc_code || "-"}</td>
                  <td className="px-3 py-2"><span className={chip(PRIO_TONE[l.priority] || PRIO_TONE.Routine)}>{l.priority || "Routine"}</span></td>
                  <td className="px-3 py-2"><span className={chip(STATUS_TONE[l.status] || STATUS_TONE.Ordered)}>{l.status || "Ordered"}</span></td>
                  <td className="px-3 py-2 text-[#7B6157]">{l.ordering_provider_name || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157] max-w-[180px] truncate" title={l.instruction}>{l.instruction || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157] max-w-[180px] truncate" title={l.indication}>{l.indication || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">
                    {l.result_value ? (
                      <div className="flex items-center gap-1">
                        <span>{l.result_value}</span>
                        {l.abnormal_flag && !l.abnormal_flag.startsWith("N") && <AlertTriangle size={12} className="text-[#C62828]" />}
                      </div>
                    ) : "-"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {editable && (
                        <>
                          <button data-testid={`edit-lab-${l.id}`} onClick={() => { setForm({ ...EMPTY, ...l }); setEditId(l.id); setPanel(true); }}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[#7B6157] hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                          <button data-testid={`delete-lab-${l.id}`} onClick={() => setDel(l)}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Lab Order" : "Add New Lab Order"} testId="lab-slide-panel">
        <form onSubmit={submit} className="space-y-4 p-1">
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Test Name *</FLabel><FInput data-testid="lab-test-name" value={form.test_name} onChange={f("test_name")} placeholder="Comprehensive Metabolic Panel" /></div>
            <div>
              <FLabel>LOINC Code</FLabel>
              <CodeAutocomplete kind="loinc" testId="lab-loinc" value={form.loinc_code}
                onChange={(v) => setForm((s) => ({ ...s, loinc_code: v }))}
                onPick={(item) => setForm((s) => ({ ...s, loinc_code: item.code,
                                                    test_name: s.test_name || item.description }))}
                placeholder="24323-8 or lipid" />
            </div>
            <div><FLabel>Order Date *</FLabel><DateField testId="lab-order-date" value={form.order_date} onChange={(v) => setForm((s) => ({ ...s, order_date: v }))} /></div>
            <div><FLabel>Ordering Provider</FLabel>
              <ProviderSelect testId="lab-provider" value={form.ordering_provider_name}
                onChange={(v) => setForm((s) => ({ ...s, ordering_provider_name: v }))}
                placeholder="Dr Harper" />
            </div>
            <div><FLabel>Priority</FLabel><FSelect data-testid="lab-priority" value={form.priority} onChange={f("priority")}>{priorities.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
            <div><FLabel>Status</FLabel><FSelect data-testid="lab-status" value={form.status} onChange={f("status")}>{statuses.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
          </div>
          <div><FLabel>Instruction</FLabel><FTextarea data-testid="lab-instruction" rows={2} value={form.instruction} onChange={f("instruction")} placeholder="Fasting required for 8 hours prior" /></div>
          <div><FLabel>Indication (Clinical Reason)</FLabel><FTextarea data-testid="lab-indication" rows={2} value={form.indication} onChange={f("indication")} placeholder="Baseline before starting lithium" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Specimen Type</FLabel><FSelect data-testid="lab-specimen-type" value={form.specimen_type} onChange={f("specimen_type")}>
              {SPECIMEN_TYPES.map((x) => <option key={x} value={x}>{x || "Not specified"}</option>)}
            </FSelect></div>
            <div><FLabel>Reference Lab</FLabel><FInput data-testid="lab-reference-lab" value={form.reference_lab} onChange={f("reference_lab")} placeholder="e.g., LabCorp Charlotte" /></div>
            <div><FLabel>Reflex Tests</FLabel><FInput data-testid="lab-reflex" value={form.reflex_tests} onChange={f("reflex_tests")} placeholder="e.g., Reflex to Free T4 if TSH abnormal" /></div>
            <div><FLabel>Auto-Repeat Interval (days)</FLabel><FInput data-testid="lab-repeat" type="number" value={form.auto_repeat_interval_days} onChange={f("auto_repeat_interval_days")} placeholder="e.g., 90 for quarterly" /></div>
            <div className="col-span-2 flex items-center gap-2">
              <input id="lab-fasting" type="checkbox" data-testid="lab-fasting" checked={!!form.fasting}
                onChange={(e) => setForm((s) => ({ ...s, fasting: e.target.checked }))} className="h-4 w-4 rounded border-[#E5DFD3] text-[#4E342E]" />
              <label htmlFor="lab-fasting" className="text-sm text-[#5D4037]">Fasting required</label>
            </div>
          </div>

          <div className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3 space-y-3">
            <div className="text-[10px] font-bold uppercase text-[#7B6157]">Result Details (fill when resulted)</div>
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Collected Date</FLabel><DateField testId="lab-collected" value={form.collected_date} onChange={(v) => setForm((s) => ({ ...s, collected_date: v }))} /></div>
              <div><FLabel>Collector Name</FLabel><FInput data-testid="lab-collector" value={form.collector} onChange={f("collector")} /></div>
              <div><FLabel>Collection Site</FLabel><FInput data-testid="lab-collection-site" value={form.collection_site} onChange={f("collection_site")} placeholder="e.g., left antecubital" /></div>
              <div><FLabel>Result Date</FLabel><DateField testId="lab-result-date" value={form.result_date} onChange={(v) => setForm((s) => ({ ...s, result_date: v }))} /></div>
              <div><FLabel>Result Value</FLabel><FInput data-testid="lab-result-value" value={form.result_value} onChange={f("result_value")} placeholder="4.2 mmol/L" /></div>
              <div><FLabel>Reference Range</FLabel><FInput data-testid="lab-range" value={form.reference_range} onChange={f("reference_range")} placeholder="3.5-5.0 mmol/L" /></div>
              <div><FLabel>Abnormal Flag</FLabel><FSelect data-testid="lab-flag" value={form.abnormal_flag} onChange={f("abnormal_flag")}>{ABNORMAL_FLAGS.map((x) => <option key={x} value={x}>{x || "(none)"}</option>)}</FSelect></div>
              <div><FLabel>Result Status</FLabel><FSelect data-testid="lab-result-status" value={form.result_status} onChange={f("result_status")}>
                {RESULT_STATUSES.map((x) => <option key={x}>{x}</option>)}
              </FSelect></div>
              <div className="col-span-2"><FLabel>Interpretation Notes</FLabel><FTextarea data-testid="lab-interpretation" rows={2} value={form.interpretation} onChange={f("interpretation")} placeholder="Clinician interpretation of result" /></div>
            </div>
          </div>
          <div><FLabel>Notes</FLabel><FTextarea data-testid="lab-notes" rows={2} value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="lab-save" type="submit">{editId ? "Save Changes" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={remove}
        title="Remove lab order" description={`Delete ${del?.test_name} (#${del?.lab_order_number})?`} />

      <SlidePanel open={trendsOpen} onClose={() => setTrendsOpen(false)} title="Lab Result Trends" testId="lab-trends-panel">
        {!trends ? <div className="h-40 animate-pulse rounded-xl bg-[#EFEBE9]" /> :
          trends.trends.length === 0 ? (
            <Empty text="No resulted labs yet. Mark labs as Resulted with a numeric value to see trends." />
          ) : (
            <div className="space-y-4 p-1" data-testid="lab-trends-content">
              <div className="rounded-xl bg-[#FDFCF8] border border-[#EFEBE0] p-3 text-xs text-[#7B6157]">
                Showing {trends.total_tests_with_results} unique test{trends.total_tests_with_results === 1 ? "" : "s"} with resulted values. Numeric results are auto-parsed for the trendline; the raw string is preserved.
              </div>
              {trends.trends.map((t, ti) => {
                const numeric = t.points.filter((p) => p.value !== null);
                return (
                  <div key={ti} data-testid={`lab-trend-${ti}`} className="rounded-2xl border border-[#EFEBE0] p-3">
                    <div className="mb-2 flex items-center gap-2 flex-wrap">
                      <div className="font-heading text-sm font-bold text-[#3E2723]">{t.test_name}</div>
                      {t.loinc_code && <span className="font-mono text-[10px] text-[#7B6157]">{t.loinc_code}</span>}
                      <span className="ml-auto rounded-full bg-[#EFEBE0] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]">{t.count} results</span>
                      {t.unit && <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-bold text-[#4E342E]">{t.unit}</span>}
                    </div>
                    {numeric.length >= 2 ? (
                      <div className="h-40" data-testid={`lab-trend-chart-${ti}`}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ReChart data={numeric.map((p) => ({ ...p, dateShort: dayjs(p.date).format("MMM D") }))} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                            <XAxis dataKey="dateShort" tick={{ fontSize: 10, fill: "#8D6E63" }} />
                            <YAxis tick={{ fontSize: 10, fill: "#8D6E63" }} width={40}
                              domain={[
                                (min) => Math.min(min, typeof t.reference_low === "number" ? t.reference_low : min),
                                (max) => Math.max(max, typeof t.reference_high === "number" ? t.reference_high : max),
                              ]} />
                            <Tooltip formatter={(v) => `${v} ${t.unit || t.reference_unit || ""}`} labelFormatter={(l, p) => p?.[0]?.payload?.date} />
                            {typeof t.reference_high === "number" && (
                              <ReferenceLine y={t.reference_high} stroke="#C62828" strokeDasharray="4 4" strokeWidth={1.5}
                                label={{ value: `High ${t.reference_high}`, position: "insideTopRight",
                                          fill: "#C62828", fontSize: 10, fontWeight: 700 }} />
                            )}
                            {typeof t.reference_low === "number" && (
                              <ReferenceLine y={t.reference_low} stroke="#1565C0" strokeDasharray="4 4" strokeWidth={1.5}
                                label={{ value: `Low ${t.reference_low}`, position: "insideBottomRight",
                                          fill: "#1565C0", fontSize: 10, fontWeight: 700 }} />
                            )}
                            <Line type="monotone" dataKey="value" stroke="#4E342E" strokeWidth={2}
                              dot={<TrendDot testKey={ti} />}
                              activeDot={{ r: 6 }} />
                          </ReChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="text-xs text-[#7B6157] mb-2">Need at least 2 numeric results to draw a trendline.</div>
                    )}
                    {(typeof t.reference_low === "number" || typeof t.reference_high === "number") && (
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid={`lab-trend-baseline-${ti}`}>
                        <span className="rounded-full bg-[#FDECEA] px-2 py-0.5 font-bold text-[#C62828]">
                          Reference range {typeof t.reference_low === "number" ? t.reference_low : "-"} to {typeof t.reference_high === "number" ? t.reference_high : "-"} {t.reference_unit || t.unit || ""}
                        </span>
                        {t.out_of_range_count > 0 && (
                          <span className="rounded-full bg-[#FFEBEE] px-2 py-0.5 font-bold text-[#C62828]">
                            {t.out_of_range_count} out-of-range result{t.out_of_range_count === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="mt-2 space-y-0.5 text-xs">
                      {t.points.map((p, pi) => (
                        <div key={pi} className="flex items-center gap-2">
                          <span className="w-24 text-[#7B6157]">{p.date ? dayjs(p.date).format("MM/DD/YYYY") : "-"}</span>
                          <span className={`font-semibold ${p.out_of_range ? "text-[#C62828]" : "text-[#3E2723]"}`}>{p.raw}</span>
                          {p.reference_range && <span className="text-[#7B6157]">Ref: {p.reference_range}</span>}
                          {(p.out_of_range || (p.abnormal_flag && !p.abnormal_flag.startsWith("N"))) && (
                            <AlertTriangle size={11} className="text-[#C62828]" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </SlidePanel>
    </div>
  );
}
